import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  FormHelperText,
  Checkbox
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';

// Importar el servicio de usuarios
import { getUsers, createUser, updateUser, User } from '../../services/userService';


const profileOptions = [
  { value: 'root', label: 'Root' },
  { value: 'superadmin', label: 'Super Administrador' },
  { value: 'admin', label: 'Administrador' },
  { value: 'mkt', label: 'Marketing' },
  { value: 'usuario', label: 'Usuario' }
];

export default function UsuariosPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<Partial<User> | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const { enqueueSnackbar } = useSnackbar();

  // Cargar usuarios
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const usersData = await getUsers();
        setUsers(usersData);
        setFilteredUsers(usersData);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setError('Error al cargar la lista de usuarios');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filtrar usuarios según el texto de búsqueda
  useEffect(() => {
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const filtered = users.filter(
        user =>
          (user.first_name && user.first_name.toLowerCase().includes(searchLower)) ||
          (user.last_name && user.last_name.toLowerCase().includes(searchLower)) ||
          user.email.toLowerCase().includes(searchLower) ||
          user.profile.toLowerCase().includes(searchLower)
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchText, users]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!editingUser?.first_name?.trim()) {
      errors.first_name = 'El nombre es requerido';
    }
    
    if (!editingUser?.last_name?.trim()) {
      errors.last_name = 'El apellido es requerido';
    }
    
    if (!editingUser?.email?.trim()) {
      errors.email = 'El correo electrónico es requerido';
    } else if (!/\S+@\S+\.\S+/.test(editingUser.email)) {
      errors.email = 'El correo electrónico no es válido';
    }
    
    if (!editingUser?.profile) {
      errors.profile = 'El perfil es requerido';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenDialog = (user?: User) => {
    setFormErrors({});
    if (user) {
      setEditingUser({ ...user });
      setIsEditing(true);
    } else {
      setEditingUser({
        first_name: '',
        last_name: '',
        email: '',
        profile: 'usuario',
        enabled: true
      });
      setIsEditing(false);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setFormErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditingUser(prev => ({ ...prev, [name]: value }));
    
    // Limpiar el error del campo cuando se modifica
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const clearError = (field: string) => {
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleProfileChange = (e: SelectChangeEvent) => {
    setEditingUser(prev => ({ ...prev, profile: e.target.value }));
    clearError('profile');
  };

  const handleReceivesXlsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingUser(prev => ({
      ...prev,
      receives_xls: e.target.checked
    }));
  };

  const handleEnabledChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingUser(prev => ({ ...prev, enabled: e.target.checked }));
  };

  const handleSaveUser = async () => {
    if (!editingUser || !validateForm()) return;

    try {
      setSaveLoading(true);
      
      if (isEditing && editingUser.id) {
        // Actualizar usuario existente
        await updateUser({
          id: editingUser.id,
          profile: editingUser.profile,
          enabled: editingUser.enabled
        });
        
        // Actualizar la lista local
        setUsers(users.map(u => u.id === editingUser.id ? { ...u, ...editingUser } as User : u));
      } else {
        // Crear nuevo usuario
        await createUser({
          email: editingUser.email!,
          first_name: editingUser.first_name!,
          last_name: editingUser.last_name!,
          profile: editingUser.profile!
        });
        
        // Recargar la lista de usuarios
        const updatedUsers = await getUsers();
        setUsers(updatedUsers);
      }
      
      enqueueSnackbar(
        `Usuario ${isEditing ? 'actualizado' : 'creado'} correctamente`, 
        { variant: 'success' }
      );
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error al guardar el usuario:', error);
      const errorMessage = error instanceof Error ? error.message : `Error al ${isEditing ? 'actualizar' : 'crear'} el usuario`;
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaveLoading(false);
    }
  };

  const columns: GridColDef[] = [
    { 
      field: 'name', 
      headerName: 'USUARIO', 
      flex: 2,
      minWidth: 250,
      renderCell: (params: any) => (
        <Box>
          <Typography variant="h6" component="div" sx={{ fontSize: '1.1rem', lineHeight: 1.2, mb: 0.5, fontWeight: 600 }}>
            {params.row.first_name} {params.row.last_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {params.row.email}
          </Typography>
        </Box>
      ),
    },
    { 
      field: 'profile', 
      headerName: 'PERFIL', 
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) => {
        const profile = profileOptions.find(p => p.value === params.value)?.label || params.value;
        return (
          <Typography variant="body2">
            {profile}
          </Typography>
        );
      },
    },
    { 
      field: 'enabled', 
      headerName: 'ESTADO', 
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: any) => {
        const isEnabled = params.row.enabled as boolean;
        return (
          <Box
            sx={{
              backgroundColor: isEnabled ? 'rgba(0, 200, 83, 0.08)' : 'rgba(244, 67, 54, 0.08)',
              color: isEnabled ? '#00A651' : '#D32F2F',
              border: `1px solid ${isEnabled ? '#00A651' : '#D32F2F'}`,
              px: 1.5,
              py: 0.4,
              borderRadius: 12,
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 80,
              height: 24,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              '&:hover': {
                backgroundColor: isEnabled ? 'rgba(0, 200, 83, 0.12)' : 'rgba(244, 67, 54, 0.12)',
              },
            }}
          >
            {isEnabled ? 'Activo' : 'Inactivo'}
          </Box>
        );
      },
    },
    { 
      field: 'receives_xls', 
      headerName: 'CONCILIACIÓN PUNTOS', 
      flex: 0.8,
      minWidth: 150,
      renderCell: (params: any) => {
        const receivesXls = params.row.receives_xls as boolean;
        
        if (!receivesXls) return null;
        
        return (
          <Box
            sx={{
              backgroundColor: 'rgba(0, 150, 136, 0.08)',
              color: '#00897B',
              border: '1px solid #00897B',
              px: 1.5,
              py: 0.4,
              borderRadius: 12,
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.3px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 120,
              height: 24,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              '&:hover': {
                backgroundColor: 'rgba(0, 150, 136, 0.12)',
              },
            }}
          >
            Recibe mail
          </Box>
        );
      },
    },
    { 
      field: 'updated_at', 
      headerName: 'ÚLTIMA ACTUALIZACIÓN', 
      flex: 1,
      minWidth: 180,
      valueFormatter: (params: { value: string }) => {
        if (!params.value) return 'N/A';
        const date = new Date(params.value);
        if (isNaN(date.getTime())) return 'Fecha inválida';
        
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      },
    },
    {
      field: 'actions',
      headerName: 'ACCIONES',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <Box display="flex" gap={1}>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDialog(params.row);
              }}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.paper' }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography 
            variant="h5" 
            component="h1" 
            sx={{ 
              fontWeight: 600, 
              color: '#C8102E',
              fontSize: '28px',
              lineHeight: 1.2,
              letterSpacing: '0.0075em',
              mb: 0,
              padding: '8px 0',
              fontFamily: 'Roboto, Arial, sans-serif'
            }}
          >
            Gestión de Usuarios
          </Typography>
          
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: 'white',
              color: '#C8102E',
              borderColor: '#C8102E',
              '&:hover': {
                backgroundColor: 'rgba(200, 16, 46, 0.04)',
                borderColor: '#A50E26',
                color: '#A50E26',
              },
            }}
          >
            Nuevo Usuario
          </Button>
        </Box>

        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <TextField
              variant="outlined"
              placeholder="Buscar por nombre, email o perfil..."
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <Tooltip title="Limpiar búsqueda">
                      <IconButton 
                        size="small" 
                        onClick={() => setSearchText('')}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{ 
                minWidth: 300, 
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={filteredUsers}
              columns={columns}
              loading={loading}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              onRowClick={(params) => handleOpenDialog(params.row as User)}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  },
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f5f5',
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                  },
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                },
                '& .MuiDataGrid-columnHeader': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-columnHeaderTitleContainer': {
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Diálogo de formulario de usuario */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth error={!!formErrors.first_name}>
                <TextField
                  fullWidth
                  label="Nombre"
                  name="first_name"
                  value={editingUser?.first_name || ''}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  size="small"
                  error={!!formErrors.first_name}
                />
                {formErrors.first_name && (
                  <FormHelperText>{formErrors.first_name}</FormHelperText>
                )}
              </FormControl>
              
              <FormControl fullWidth error={!!formErrors.last_name}>
                <TextField
                  fullWidth
                  label="Apellido"
                  name="last_name"
                  value={editingUser?.last_name || ''}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  size="small"
                  error={!!formErrors.last_name}
                />
                {formErrors.last_name && (
                  <FormHelperText>{formErrors.last_name}</FormHelperText>
                )}
              </FormControl>
            </Box>

            <FormControl fullWidth error={!!formErrors.email}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={editingUser?.email || ''}
                onChange={handleInputChange}
                margin="normal"
                variant="outlined"
                size="small"
                disabled={isEditing}
                error={!!formErrors.email}
              />
              {formErrors.email && (
                <FormHelperText>{formErrors.email}</FormHelperText>
              )}
            </FormControl>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <FormControl fullWidth error={!!formErrors.profile}>
                <InputLabel id="profile-label">Perfil</InputLabel>
                <Select
                  labelId="profile-label"
                  value={editingUser?.profile || ''}
                  label="Perfil"
                  onChange={handleProfileChange}
                  onFocus={() => clearError('profile')}
                >
                  {profileOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.profile && (
                  <FormHelperText>{formErrors.profile}</FormHelperText>
                )}
              </FormControl>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingUser?.enabled ?? true}
                      onChange={handleEnabledChange}
                      color="primary"
                    />
                  }
                  label={editingUser?.enabled ? 'Activo' : 'Inactivo'}
                  sx={{ 
                    '& .MuiFormControlLabel-label': {
                      minWidth: 60
                    }
                  }}
                />
              </Box>
              
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Button 
            onClick={handleCloseDialog} 
            color="inherit"
            sx={{ mr: 1 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{
              backgroundColor: '#C8102E',
              '&:hover': {
                backgroundColor: '#A50E26',
              },
            }}
          >
            {saveLoading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
